import Box from '@mui/material/Box';

import { brand } from '../theme';

/**
 * The brand wordmark, as real text.
 *
 * Three lines, matching the drawn logo:
 *
 *      TOP TILE                  <- TOP charcoal, TILE gold
 *   — CONSTRUCTION LTD. —        <- gold dashes either side
 *   QUALITY TILE WORK . BUILT TO LAST
 *
 * Sizes, weights, and letter-spacing come from the logo's Figma measurements,
 * converted to em against the CONSTRUCTION line (27.4px in the source file) so
 * the whole lockup scales with one font-size on the root element. Search
 * engines and screen readers get the words themselves — which is the point of
 * this component existing instead of an <img>.
 *
 * One deliberate difference from the drawn artwork: the SVG says "TILES", and
 * docs/instructions.md records that as a known error in the supplied logo. The
 * registered name is Top Tile Construction LTD., so the text says TILE.
 *
 * Written in normal case and uppercased by CSS — some screen readers spell out
 * words written in caps letter by letter.
 *
 * Each line carries data-wm-line so a parent can animate the lockup line by
 * line. Nothing here is hidden by default; animation is the caller's business.
 */

const goldDash = {
	display: 'inline-block',
	width: '1.41em',
	height: '0.09em',
	minHeight: '2px',
	backgroundColor: brand.accent,
	flex: 'none',
};

export default function Wordmark({ component = 'div', sx = {} }) {
	return (
		<Box
			component={component}
			sx={{
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				textTransform: 'uppercase',
				lineHeight: 1,
				color: brand.dark,
				// Headings arrive with margins and h1 font-size; this is a lockup.
				m: 0,
				fontSize: 'inherit',
				fontWeight: 'normal',
				...sx,
			}}
		>
			{/* TOP TILE */}
			<Box
				data-wm-line
				component="span"
				sx={{
					display: 'block',
					fontSize: '1.91em',
					fontWeight: 800,
					letterSpacing: '0.045em',
					whiteSpace: 'nowrap',
				}}
			>
				Top{' '}
				<Box component="span" sx={{ color: brand.accent }}>
					Tile
				</Box>
			</Box>
			{' '}
			{/* — CONSTRUCTION LTD. — */}
			<Box
				data-wm-line
				component="span"
				sx={{
					display: 'flex',
					alignItems: 'center',
					gap: '0.55em',
					marginTop: '0.5em',
					fontSize: '1em',
					fontWeight: 600,
					whiteSpace: 'nowrap',
				}}
			>
				<Box component="span" aria-hidden="true" sx={goldDash} />
				<Box component="span" sx={{ letterSpacing: '0.175em' }}>
					Construction
				</Box>{' '}
				<Box component="span" sx={{ letterSpacing: '0.04em' }}>
					Ltd.
				</Box>
				<Box component="span" aria-hidden="true" sx={goldDash} />
			</Box>
			{' '}
			{/* QUALITY TILE WORK . BUILT TO LAST */}
			<Box
				data-wm-line
				component="span"
				sx={{
					display: 'flex',
					alignItems: 'baseline',
					gap: '0.55em',
					marginTop: '0.62em',
					fontSize: '0.545em',
					fontWeight: 500,
					letterSpacing: '0.13em',
					whiteSpace: 'nowrap',
				}}
			>
				{/* "Tile Work" in gold, exactly as the drawn logo sets it. */}
				<Box component="span">
					Quality{' '}
					<Box component="span" sx={{ color: brand.accent }}>
						Tile Work
					</Box>
				</Box>
				<Box component="span" sx={{ fontWeight: 800 }}>
					.
				</Box>{' '}
				<Box component="span">Built to Last</Box>
			</Box>
		</Box>
	);
}
